import React from "react";
import emptyStateTokens from "./EmptyState.tokens";

/**
 * The line-icon set for the empty-state block, drawn on the design's 24-box at
 * 1.85 stroke. Path data for the first seven comes straight from
 * `Empty State.dc.html`; the rest follow the same construction so a module the
 * mock didn't enumerate still reads as part of the set.
 */
const PATHS = {
  doc: ["M7 3h7l4 4v14H7z", "M14 3v4h4"],
  template: ["M4 5h16v14H4z", "M4 9h16M9 9v10"],
  contact: [
    "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    "M4 21c0-4 3.6-6 8-6s8 2 8 6",
  ],
  team: [
    "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
    "M2 20c0-3.3 3.1-5 7-5s7 1.7 7 5",
    "M17 8.5a2.8 2.8 0 1 0 0-5.6",
    "M18 20c0-2.6-.9-4.2-2.4-5.2",
  ],
  invoice: ["M6 3h12v18l-3-2-3 2-3-2-3 2z", "M9 8h6M9 12h6"],
  ticket: ["M4 8h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4z", "M12 8v10"],
  report: ["M4 20h16", "M7 20V11M12 20V6M17 20v-6"],
  /** Filtered or searched empty — a magnifier, never a first-use state. */
  search: ["M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z", "M20 20l-4-4"],
  /** API keys and credentials. */
  key: [
    "M15 4a5 5 0 1 1-4.6 7L4 17.4V20h2.6l1-1h2v-2h2l1.4-1.4A5 5 0 0 1 15 4z",
    "M16.5 8h.01",
  ],
  /** Connectors, webhooks, integrations. */
  link: [
    "M10 13a4 4 0 0 0 5.7.4l3-3A4 4 0 0 0 13 4.7l-1.7 1.7",
    "M14 11a4 4 0 0 0-5.7-.4l-3 3A4 4 0 0 0 11 19.3l1.7-1.7",
  ],
  /** A received queue — the inbox and notification lists. */
  inbox: ["M4 13h4l1.5 3h5L16 13h4", "M4 13l2.5-8h11L20 13v6H4z"],
  /** Audit trails and activity feeds. */
  activity: ["M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z", "M12 7v5l3.2 2"],
  /** Network allowlists. */
  shield: ["M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6z"],
  /** Folders and collections. */
  folder: ["M4 6h5.5l2 2.5H20V19H4z"],
} as const;

export type EmptyStateIconName = keyof typeof PATHS;

interface EmptyStateIconProps {
  name: EmptyStateIconName;
  stroke: string;
  size: number;
}

const EmptyStateIcon: React.FC<EmptyStateIconProps> = ({
  name,
  stroke,
  size,
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={emptyStateTokens.tile.iconStrokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: size, height: size }}
    aria-hidden="true"
    focusable="false"
  >
    {(PATHS[name] ?? PATHS.doc).map((d, index) => (
      <path key={index} d={d} />
    ))}
  </svg>
);

export default EmptyStateIcon;
